import {useState} from 'react'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {Button} from '@/components/ui/button/Button'
import {DotsHorizontalIcon, Download01Icon, CheckIcon} from '@/components/ui/icon/Icon'
import {Menu, MenuGroup, MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {buildFile, downloadFile} from '@/utilities/helpers'
import {SiarExport} from '@/features/projects/siar-bulk-export/types'
import {httpReDownloadSiarExport} from '@/features/projects/siar-bulk-export/http/siarBulkExport.http'
import {ConfirmTransmissionDialog} from '@/features/projects/siar-bulk-export/components/confirm-transmission-dialog/ConfirmTransmissionDialog'

export const SiarExportRowMenu = ({siarExport}: {siarExport: SiarExport}) => {
    const {t} = useTranslation()
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)

    const canDownload = siarExport.status !== 'transmitted'
    const canConfirmTransmission = siarExport.status !== 'transmitted'

    const handleDownload = async () => {
        setIsDownloading(true)
        try {
            const blob = await httpReDownloadSiarExport({id: siarExport.id})
            const fileName = siarExport.fileName.replace(/\.zip$/i, '')
            const file = buildFile({
                extension: 'zip',
                fileName,
                data: blob
            })
            downloadFile(file)
        } catch {
            toast.error(t('errors:default'))
        } finally {
            setIsDownloading(false)
        }
    }

    if (!canDownload && !canConfirmTransmission) {
        return null
    }

    return (
        <>
            <MenuProvider open={isMenuOpen} setOpen={setIsMenuOpen}>
                <MenuButton
                    render={
                        <Button
                            className={isMenuOpen ? undefined : 'show-on-hover'}
                            size="xs"
                            shape="square"
                            variant="secondary"
                            onClick={event => event.stopPropagation()}
                        >
                            <DotsHorizontalIcon />
                        </Button>
                    }
                />
                <Menu width="240px" onClick={event => event.stopPropagation()}>
                    <MenuGroup>
                        {canDownload && (
                            <MenuItem onClick={handleDownload} disabled={isDownloading}>
                                <Download01Icon />
                                {t('projects:bulk_siar_export_redownload')}
                                {isDownloading && <Spinner size={12} />}
                            </MenuItem>
                        )}
                        {canConfirmTransmission && (
                            <MenuItem onClick={() => setIsConfirmDialogOpen(true)}>
                                <CheckIcon />
                                {t('projects:bulk_siar_export_confirm_transmission')}
                            </MenuItem>
                        )}
                    </MenuGroup>
                </Menu>
            </MenuProvider>
            {isConfirmDialogOpen && (
                <ConfirmTransmissionDialog
                    siarExport={siarExport}
                    isOpen={isConfirmDialogOpen}
                    onClose={() => setIsConfirmDialogOpen(false)}
                />
            )}
        </>
    )
}
